import React from "react";
import { BadgeCheck, ChefHat, CheckCircle2, Clock, HandPlatter, ImageOff } from "lucide-react";

import Avatar from "./Avatar";
import { medidaDoItem, qtdDoItem } from "../../lib/unidadeVenda";

const dinheiro = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const hora = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
};

const ETAPAS = {
  enviado: { rotulo: "Enviado", icone: Clock, cor: "text-zinc-400" },
  cozinha: { rotulo: "Na cozinha", icone: ChefHat, cor: "text-amber-500" },
  pronto: { rotulo: "Pronto", icone: CheckCircle2, cor: "text-emerald-500" },
  entregue: { rotulo: "Entregue", icone: CheckCircle2, cor: "text-emerald-500" },
};

// Situação de cada item marcada no KDS. "Em produção" não aparece mais no
// item (pedido do dono, 23/09): repetia o "Na cozinha" do cabeçalho da rodada.
// Fica só o que o cabeçalho não conta — o prato que já saiu ou já chegou.
const SITUACAO_ITEM = {
  pronto: { texto: "a caminho", cor: "text-sky-500" },
  entregue: { texto: "entregue ✓", cor: "text-emerald-500" },
};

function FotoItem({ foto, fotoDe, darkMode }) {
  return (
    <span className={`w-12 h-12 rounded-xl overflow-hidden shrink-0 flex items-center justify-center ${darkMode ? "bg-zinc-800 text-zinc-600" : "bg-zinc-100 text-zinc-400"}`}>
      {foto ? (
        <img src={fotoDe ? fotoDe(foto) : foto} alt="" className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <ImageOff className="w-5 h-5" strokeWidth={1.5} />
      )}
    </span>
  );
}

// Uma rodada da comanda: o rosto e o nome de quem mandou, a etapa da rodada e
// os produtos com foto. O que o garçom lançou pelo painel para uma pessoa
// aparece como dela, com a marca "lançado pelo garçom" (dono, 24/09/2026);
// sem dono, aparece como "Mesa". Produto pago ganha o selo "pago ✓"; pago em
// parte (divisão, valor digitado no caixa), "pago R$ X · falta R$ Y".
// Quilo/litro (30/09/2026): peso sai "1,250 kg". No painel a comanda passa o
// cadastro (`produtos`, casado pelo produto_id); no celular do cliente vale o
// que o item traz.
export default function RodadaComanda({ rodada, fotoDe, darkMode, produtos }) {
  const etapa = ETAPAS[rodada.etapa] || ETAPAS.enviado;
  const Icone = etapa.icone;
  const balcao = !rodada.cliente_id;
  const cartao = darkMode ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200";

  return (
    <div className={`rounded-2xl border px-3.5 py-3 ${cartao} ${rodada.meu ? "ring-2 ring-primary/30" : ""}`}
         data-e2e={rodada.meu ? "mesa-rodada-minha" : "mesa-rodada-mesa"}>
      <div className="flex items-center gap-2 mb-2.5">
        <Avatar nome={rodada.quem} foto={rodada.quem_foto} fotoDe={fotoDe} tamanho={28} balcao={balcao} />
        <span className="text-sm font-black truncate">{rodada.meu ? "Você" : rodada.quem}</span>
        {hora(rodada.criado_em) ? <span className="text-[11px] opacity-50">{hora(rodada.criado_em)}</span> : null}
        {rodada.garcom && rodada.lancado_por ? (
          // Só a comanda do painel recebe quem lançou (o celular do cliente não)
          <span className="flex items-center gap-0.5 text-[10px] font-bold text-violet-500 bg-violet-500/10 rounded-full px-1.5 py-0.5 min-w-0"
                data-e2e="mesa-rodada-lancado-por">
            <HandPlatter className="w-3 h-3 shrink-0" />
            <span className="truncate">lançado por {rodada.lancado_por.nome || "Equipe"}</span>
          </span>
        ) : rodada.garcom ? (
          <span className="flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-500 bg-violet-500/10 rounded-full px-1.5 py-0.5"
                data-e2e="mesa-rodada-garcom">
            <HandPlatter className="w-3 h-3" /> garçom
          </span>
        ) : null}
        <span className={`ml-auto flex items-center gap-1 text-[11px] font-bold shrink-0 ${etapa.cor}`}>
          <Icone className="w-3.5 h-3.5" /> {etapa.rotulo}
        </span>
      </div>
      <div className="space-y-2">
        {rodada.itens.map((item, idx) => {
          const sit = SITUACAO_ITEM[item.situacao];
          return (
            <div key={idx} className="flex items-center gap-3">
              <FotoItem foto={item.foto} fotoDe={fotoDe} darkMode={darkMode} />
              <div className="flex-1 min-w-0">
                <p className="text-sm leading-tight">
                  <span className="font-black text-primary">{qtdDoItem(item.quantidade || 1, medidaDoItem(item.produto_id ? { ...item, id: item.produto_id } : item, produtos))} </span>
                  <span className="font-semibold">{item.nome}</span>
                </p>
                {item.subitems?.length ? <p className="text-[11px] opacity-60 line-clamp-2">{item.subitems.join(" • ")}</p> : null}
                {item.observacao ? <p className="text-[11px] opacity-60 truncate">📝 {item.observacao}</p> : null}
                {item.pago ? (
                  <p className="text-[11px] font-bold text-emerald-500 flex items-center gap-0.5" data-e2e="mesa-item-pago">
                    <BadgeCheck className="w-3.5 h-3.5" /> pago
                  </p>
                ) : item.parcial > 0 ? (
                  <p className="text-[11px] font-bold text-amber-500" data-e2e="mesa-item-parcial">
                    pago {dinheiro(item.parcial)} · falta {dinheiro(item.restante)}
                  </p>
                ) : null}
                {sit ? (
                  <p className={`text-[11px] font-semibold ${sit.cor}`} data-e2e={`mesa-item-${item.situacao}`}>
                    {sit.texto}{item.situacao === "entregue" && hora(item.entregue_at) ? ` ${hora(item.entregue_at)}` : ""}
                  </p>
                ) : null}
              </div>
              <span className={`text-sm shrink-0 ${item.pago ? "line-through opacity-40" : "opacity-70"}`}>{dinheiro(item.subtotal)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
